import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { useNotification } from '@adsk/bim360-matrix-react-notification-manager/es/helpers/context';
import {
  NotificationLevels,
  NotificationTypes
} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import useEntitySearch from './helpers/hooks/useEntitySearch';
import useEntitySelection from './helpers/hooks/useEntitySelection';
import useCanCreate from './helpers/hooks/useCanCreate';
import EntityPickerModal from './EntityPickerModal';
import TryAgainNotificationMessage from './TryAgainNotificationMessage';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA, ENTITY_TYPES } from './helpers/consts';
import { pluginShape } from './helpers/propTypes';

const EntityPickerModalContainer = ({
  container,
  selectedType,
  plugin,
  isOpen,
  closeModal,
  onAddRelationships,
  sourceEntityType,
  sourceEntityId,
  relationships,
  isLoadingRelationships,
  errorLoadingRelationships,
  reloadRelationships,
  addEntity,
  addRelationshipsToLocalState,
  title,
  createFormProps
}) => {
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const { showNotification } = useNotification();
  const {
    entities: searchedEntities,
    isLoading: isLoadingEntities,
    error: errorLoadingEntities,
    onTryAgain: reloadEntities,
    onLoadMore,
    searchExpression,
    setSearchExpression
  } = useEntitySearch({ plugin, entityType: selectedType, pageLimit: 15 });
  const { selectedEntities, onSelectionChange, clearSelection } = useEntitySelection();
  const [isSaving, setIsSaving] = useState(false);
  const {
    canCreate,
    isLoading: isLoadingCanCreate,
    error: errorLoadingCanCreate,
    retry: retryLoadingCanCreate
  } = useCanCreate(plugin, createFormProps);

  useEffect(() => {
    if (!selectedType) {
      clearSelection();
      setSearchExpression('');
    }
  }, [clearSelection, selectedType, setSearchExpression]);
  const manuallySelectedIds = selectedEntities.map(e => e.id);
  const linkedEntityIds = relationships.map(r => r.entityId);
  const searchedIds = searchedEntities.map(e => e.id);
  const selectedNotMatched = selectedEntities.filter(e => !searchedIds.includes(e.id));
  // We can't show entities before getting relationships, so we could mark already linked ones.
  const entities =
    isLoadingRelationships || errorLoadingRelationships
      ? []
      : [...selectedNotMatched, ...searchedEntities];

  const addRelationships = (sourceEntity, entitiesToLink) => {
    setIsSaving(true);
    const isPlural = entitiesToLink.length > 1;
    const typeText = isPlural
      ? T.translate(`${selectedType}_PLURAL`)
      : T.translate(`${selectedType}_SHORT_NAME`);
    const handleSave = () =>
      onAddRelationships(sourceEntity, entitiesToLink)
        .then(newRelationships => {
          addRelationshipsToLocalState(newRelationships, selectedEntities);
          closeModal();
          setIsSaving(false);
        })
        .catch(error => {
          console.error('Error adding relationships', error);
          showNotification({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: (
              <TryAgainNotificationMessage onTryAgain={handleSave}>
                {T.translate('ERROR_ADDING_RELATIONSHIPS_MESSAGE', { type: typeText })}
              </TryAgainNotificationMessage>
            )
          });
          setIsSaving(false);
        });
    handleSave();
  };

  const onSave = () => {
    const sourceEntity = {
      id: sourceEntityId,
      ...ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[sourceEntityType]
    };
    const entitiesToLink = selectedEntities.map(e => ({
      id: e.id,
      ...ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[e.entityType]
    }));
    addRelationships(sourceEntity, entitiesToLink);
  };

  const onCreateSuccess = entity => {
    const sourceEntity = {
      id: sourceEntityId,
      ...ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[sourceEntityType]
    };
    const entityToLink = {
      id: entity.id,
      ...ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[entity.entityType]
    };
    addEntity(entity);
    addRelationships(sourceEntity, [entityToLink]);
  };

  const isLoading = isLoadingRelationships || isLoadingEntities || isLoadingCanCreate;
  const error = !!errorLoadingRelationships || !!errorLoadingEntities || !!errorLoadingCanCreate;
  const onTryAgain = error
    ? () => {
      errorLoadingRelationships && reloadRelationships();
      errorLoadingEntities && reloadEntities();
      errorLoadingCanCreate && retryLoadingCanCreate();
    }
    : undefined;

  return (
    <EntityPickerModal
      targetEntityType={selectedType}
      show={isOpen}
      onExited={closeModal}
      onCancel={closeModal}
      onSave={onSave}
      onCreateSuccess={onCreateSuccess}
      isSaving={isSaving}
      canCreate={canCreate}
      plugin={plugin}
      container={container}
      searchExpression={searchExpression}
      onSearchChange={setSearchExpression}
      isLoading={isLoading}
      error={error}
      onTryAgain={onTryAgain}
      onLoadMore={onLoadMore}
      manuallySelectedIds={manuallySelectedIds}
      linkedEntityIds={linkedEntityIds}
      entities={entities}
      clearSelectedEntities={clearSelection}
      onChange={onSelectionChange}
      title={title}
      createFormProps={createFormProps}
    />
  );
};

EntityPickerModalContainer.propTypes = {
  /**
   * The entity ID we want to link to.
   */
  sourceEntityId: PropTypes.string.isRequired,
  /**
   * The source entity type.
   */
  sourceEntityType: PropTypes.oneOf(objectValues(ENTITY_TYPES)).isRequired,
  /**
   * Selected type of entities to allow picking from.
   */
  selectedType: PropTypes.oneOf(objectValues(ENTITY_TYPES)),
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape,
  /**
   * Whether or not the modal is open.
   */
  isOpen: PropTypes.bool,
  /**
   * Handler for closing the modal.
   */
  closeModal: PropTypes.func,
  /**
   * Handler to add entity relationships. Should return a promise for add relationship request, resolving to the newly created relationships.
   * Receives two argument:
   * 1. The source entity to link all entities to, of shape: {domain, id, type};
   * 2. An array of entities to link to, of shape: [{domain, id, type}, ...]
   */
  onAddRelationships: PropTypes.func.isRequired,
  /**
   * List of relationships between source entity and entities of `selectedType`.
   * Needed to mark currently selected entities.
   */
  relationships: PropTypes.array,
  /**
   * Whether relationships are currently loading.
   */
  isLoadingRelationships: PropTypes.bool,
  /**
   * Whether relationships load had an error.
   */
  errorLoadingRelationships: PropTypes.bool,
  /**
   * Relaod relationships
   */
  reloadRelationships: PropTypes.func,
  /**
   * A handle method to add newly created relationships to local state, to show them in entity blocks.
   */
  addRelationshipsToLocalState: PropTypes.func,
  /**
   * A handle method to add newly created entity to local state.
   */
  addEntity: PropTypes.func,
  /**
   * Modal title.
   */
  title: PropTypes.string,
  /**
   * Modal container.
   */
  container: PropTypes.object,
  /**
   * Additional props to pass to create form.
   */
  createFormProps: PropTypes.object
};

export default EntityPickerModalContainer;
